import { BaseWorkflowProcessor } from './BaseWorkflowProcessor';
import {
    CreateField,
    CreateProduct,
    CreateWorkflowData,
    WorkflowConfig,
    WorkflowConfigField
} from '@monorepo/interfaces';

/**
 * Processor implementation for creating new workflows
 *
 * @description This class implements the workflow processor for the create operation.
 * It builds CreateWorkflowData objects from form data without needing to reference any
 * existing data or preserve IDs.
 */
export class CreateWorkflowProcessor extends BaseWorkflowProcessor<CreateWorkflowData, CreateField, CreateProduct> {
    /**
     * Initializes an empty array for collecting create field objects.
     * @returns {CreateField[]} An empty array of CreateField objects.
     */
    getWorkflowDetailsFields(): CreateField[] {
        return [];
    }

    /**
     * Initializes an empty map for collecting create product objects.
     * @returns {Map<string, CreateProduct>} An empty Map of CreateProduct objects.
     */
    getWorkflowDetailsProducts(): Map<string, CreateProduct> {
        return new Map<string, CreateProduct>();
    }

    /**
     * Creates a new field object from configuration and form value
     *
     * @param fieldConfig {WorkflowConfigField} - The field configuration from the workflow definition
     * @param value {any} - The value extracted from the form control
     * @returns A CreateField object with the name, type and value populated
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    createFieldObject(fieldConfig: WorkflowConfigField, value: any): CreateField {
        return {
            name: fieldConfig.name,
            type: fieldConfig.fieldType,
            value: value
        };
    }

    /**
     * Creates a new product object containing the specified fields
     *
     * @param fields {CreateField[]} - Array of field objects to include in the product
     * @param name {string} - The name of the product
     * @returns A CreateProduct object with the provided fields
     */
    createProductObject(fields: CreateField[], name: string): CreateProduct {
        return { fields, name };
    }

    /**
     * Creates the final create workflow data object
     *
     * @param config {WorkflowConfig} - The workflow configuration used for processing
     * @param fields {CreateField[]} - Collection of processed field objects
     * @param products {CreateProduct[]} - Collection of processed product objects
     * @returns A CreateWorkflowData object ready for submission to create a new workflow
     */
    createResultObject(config: WorkflowConfig, fields: CreateField[], products: CreateProduct[]): CreateWorkflowData {
        // Extract Snowflake search parameters from fields
        const searchCoreMlfb = fields.find(field => field.name === 'search-core-mlfb')?.value as string;
        const gckSearch = fields.find(field => field.name === 'gck-search')?.value as string;
        const pckSearch = (fields.find(field => field.name === 'pck-search')?.value as string)
            .split(',')
            .filter((item: string) => item.trim().length > 0)
            .map((item: string) => item.trim());

        const priceGroupSearch = (fields.find(field => field.name === 'price-group-search')?.value as string)
            .split(',')
            .filter((item: string) => item.trim().length > 0)
            .map((item: string) => {
                const trimmed = item.trim();
                // Values with parentheses like "(leer)" or "(empty)"
                // are UI labels for "no price group selected" and should be converted to empty string
                const isEmptySelectionLabel = trimmed.startsWith('(') && trimmed.endsWith(')');
                return isEmptySelectionLabel ? '' : trimmed;
            });

        // Convert search-core-mlfb to coreMlfbs array (comma-separated values)
        const coreMlfbs = searchCoreMlfb
            ? searchCoreMlfb
                  .split(',')
                  .map(item => item.trim())
                  .filter(item => item.length > 0)
            : undefined;

        return {
            name: config.name,
            configType: config.type,
            configId: config.id,
            description: fields.find(field => field.name === 'description')?.value || '',
            startDate: new Date(fields.find(field => field.name === 'start-date')?.value || ''),
            endDate: new Date(fields.find(field => field.name === 'end-date')?.value || ''),
            fields,
            products,
            // Include Snowflake search parameters if available
            ...(gckSearch && { gck: gckSearch }),
            ...(pckSearch && { pck: pckSearch }),
            ...(priceGroupSearch && { priceGroup: priceGroupSearch }),
            ...(coreMlfbs && coreMlfbs.length > 0 && { coreMlfbs })
        };
    }

    /**
     * Finds or creates product by productName and aggregates fields for it.
     * @param {Map<string, CreateProduct>} products - Map of current products.
     * @param {string} productName - Name of the product to find or create.
     * @returns {CreateProduct} Product entry for aggregation.
     */
    private getOrCreateProduct(products: Map<string, CreateProduct>, productName: string): CreateProduct {
        if (!products.has(productName)) {
            products.set(productName, { name: productName, fields: [] });
        }
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        return products.get(productName)!;
    }

    /**
     * Handles product creation and field aggregation for 'create'.
     * Product name is always the value of the 'coreMlfb' field.
     * @param {CreateField} field - The processed field object.
     * @param {Map<string, CreateProduct>} products - Products to aggregate into.
     * @returns {void}
     */
    protected override addFieldToProduct(field: CreateField, products: Map<string, CreateProduct>): void {
        this.coreMlfbControlValue.split(',').forEach(productName => {
            const trimmedProductName = productName.trim();
            if (!trimmedProductName) return; // Skip empty names
            const product = this.getOrCreateProduct(products, trimmedProductName);
            product.fields.push(field);
        });
    }
}
